// Generador mínimo de archivos Excel (.xlsx) sin librerías: una o varias hojas con encabezado en negrilla, textos y números.
// El .xlsx es un ZIP con archivos XML; aquí los archivos van sin comprimir (método "stored"), que Excel abre sin problema.
// Se evita la librería SheetJS del prototipo: la versión publicada en npm (0.18.5) tiene vulnerabilidades conocidas.
import { leerVcf } from './vcard';

export type Celda = string | number | null | undefined;
export interface Hoja { nombre: string; filas: Record<string, Celda>[] }

const TABLA_CRC = (() => {
  const t = new Uint32Array(256);
  for (let n = 0; n < 256; n++) { let c = n; for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1; t[n] = c >>> 0; }
  return t;
})();
export function crc32(datos: Uint8Array): number {
  let c = 0xffffffff;
  for (let i = 0; i < datos.length; i++) c = TABLA_CRC[(c ^ datos[i]) & 0xff] ^ (c >>> 8);
  return (c ^ 0xffffffff) >>> 0;
}

// ZIP sin compresión: encabezado local + datos de cada archivo, directorio central y cierre.
export function zip(archivos: { nombre: string; datos: Uint8Array }[]): Uint8Array {
  const enc = new TextEncoder(), partes: Uint8Array[] = [], central: Uint8Array[] = [];
  let desplazamiento = 0;
  for (const a of archivos) {
    const nombre = enc.encode(a.nombre), crc = crc32(a.datos), n = a.datos.length;
    const local = new DataView(new ArrayBuffer(30));
    local.setUint32(0, 0x04034b50, true); local.setUint16(4, 20, true); local.setUint16(6, 0x0800, true); local.setUint16(8, 0, true);
    local.setUint16(10, 0, true); local.setUint16(12, 0x21, true); local.setUint32(14, crc, true); local.setUint32(18, n, true); local.setUint32(22, n, true);
    local.setUint16(26, nombre.length, true); local.setUint16(28, 0, true);
    partes.push(new Uint8Array(local.buffer), nombre, a.datos);
    const cen = new DataView(new ArrayBuffer(46));
    cen.setUint32(0, 0x02014b50, true); cen.setUint16(4, 20, true); cen.setUint16(6, 20, true); cen.setUint16(8, 0x0800, true); cen.setUint16(10, 0, true);
    cen.setUint16(12, 0, true); cen.setUint16(14, 0x21, true); cen.setUint32(16, crc, true); cen.setUint32(20, n, true); cen.setUint32(24, n, true);
    cen.setUint16(28, nombre.length, true); cen.setUint32(42, desplazamiento, true);
    central.push(new Uint8Array(cen.buffer), nombre);
    desplazamiento += 30 + nombre.length + n;
  }
  const tamCentral = central.reduce((s, p) => s + p.length, 0), fin = new DataView(new ArrayBuffer(22));
  fin.setUint32(0, 0x06054b50, true); fin.setUint16(8, archivos.length, true); fin.setUint16(10, archivos.length, true);
  fin.setUint32(12, tamCentral, true); fin.setUint32(16, desplazamiento, true);
  const todo = [...partes, ...central, new Uint8Array(fin.buffer)], out = new Uint8Array(todo.reduce((s, p) => s + p.length, 0));
  let i = 0; for (const p of todo) { out.set(p, i); i += p.length; }
  return out;
}

const xml = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')
  // Caracteres de control que XML no admite (pueden venir pegados desde WhatsApp).
  .replace(/[\u0000-\u0008\u000b\u000c\u000e-\u001f]/g, '');
const columna = (i: number) => { let s = ''; for (i++; i > 0; i = Math.floor((i - 1) / 26)) s = String.fromCharCode(65 + ((i - 1) % 26)) + s; return s; };
const nombreHoja = (s: string) => xml(s.replace(/[\\/?*[\]:]/g, ' ').slice(0, 31) || 'Hoja');

function hojaXml(h: Hoja): string {
  const cols = [...new Set(h.filas.flatMap(f => Object.keys(f)))];
  const celda = (v: Celda, ref: string, estilo = '') => {
    if (v == null || v === '') return '';
    if (typeof v === 'number' && isFinite(v)) return `<c r="${ref}"${estilo}><v>${v}</v></c>`;
    return `<c r="${ref}" t="inlineStr"${estilo}><is><t xml:space="preserve">${xml(String(v))}</t></is></c>`;
  };
  const anchos = cols.map(c => Math.min(60, Math.max(c.length, ...h.filas.map(f => String(f[c] ?? '').length)) + 2));
  const filas = [
    `<row r="1">${cols.map((c, j) => celda(c, columna(j) + 1, ' s="1"')).join('')}</row>`,
    ...h.filas.map((f, i) => `<row r="${i + 2}">${cols.map((c, j) => celda(f[c], columna(j) + (i + 2))).join('')}</row>`),
  ];
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"><sheetViews><sheetView workbookViewId="0"><pane ySplit="1" topLeftCell="A2" activePane="bottomLeft" state="frozen"/></sheetView></sheetViews>`
    + (cols.length ? `<cols>${anchos.map((w, j) => `<col min="${j + 1}" max="${j + 1}" width="${w}" customWidth="1"/>`).join('')}</cols>` : '')
    + `<sheetData>${filas.join('')}</sheetData></worksheet>`;
}

export function libroExcel(hojas: Hoja[]): Uint8Array {
  const enc = new TextEncoder(), a = (nombre: string, s: string) => ({ nombre, datos: enc.encode(s) });
  const cab = '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n';
  return zip([
    a('[Content_Types].xml', cab + `<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types"><Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/><Default Extension="xml" ContentType="application/xml"/><Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/><Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>${hojas.map((_, i) => `<Override PartName="/xl/worksheets/sheet${i + 1}.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>`).join('')}</Types>`),
    a('_rels/.rels', cab + `<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/></Relationships>`),
    a('xl/workbook.xml', cab + `<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"><sheets>${hojas.map((h, i) => `<sheet name="${nombreHoja(h.nombre)}" sheetId="${i + 1}" r:id="rId${i + 1}"/>`).join('')}</sheets></workbook>`),
    a('xl/_rels/workbook.xml.rels', cab + `<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">${hojas.map((_, i) => `<Relationship Id="rId${i + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet${i + 1}.xml"/>`).join('')}<Relationship Id="rId${hojas.length + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/></Relationships>`),
    a('xl/styles.xml', cab + `<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"><fonts count="2"><font><sz val="11"/><name val="Calibri"/></font><font><b/><sz val="11"/><name val="Calibri"/></font></fonts><fills count="2"><fill><patternFill patternType="none"/></fill><fill><patternFill patternType="gray125"/></fill></fills><borders count="1"><border><left/><right/><top/><bottom/><diagonal/></border></borders><cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs><cellXfs count="2"><xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/><xf numFmtId="0" fontId="1" fillId="0" borderId="0" xfId="0" applyFont="1"/></cellXfs><cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles></styleSheet>`),
    ...hojas.map((h, i) => a(`xl/worksheets/sheet${i + 1}.xml`, hojaXml(h))),
  ]);
}

// Tabla en texto separado por tabuladores, para "Copiar tabla" y pegar en Excel o Google Sheets.
export function aTSV(filas: Record<string, Celda>[]): string {
  const cols = [...new Set(filas.flatMap(f => Object.keys(f)))];
  const t = (v: Celda) => String(v ?? '').replace(/[\t\r\n]+/g, ' ');
  return [cols.join('\t'), ...filas.map(f => cols.map(c => t(f[c])).join('\t'))].join('\n');
}

// ---------- Lectura ----------
// Lee .xlsx (el ZIP que guarda Excel, comprimido o no) y .csv en el navegador. Devuelve cada hoja como filas de celdas:
// textos, números o fechas ISO ("2023-03-15", si la celda tiene formato de fecha). Los .xls viejos no se leen.
export type CeldaLeida = string | number | null;
export interface HojaLeida { nombre: string; filas: CeldaLeida[][] }

async function descomprimir(datos: Uint8Array): Promise<Uint8Array> {
  const flujo = new Blob([datos as BlobPart]).stream().pipeThrough(new DecompressionStream('deflate-raw'));
  return new Uint8Array(await new Response(flujo).arrayBuffer());
}
export async function abrirZip(buf: Uint8Array): Promise<Map<string, Uint8Array>> {
  const v = new DataView(buf.buffer, buf.byteOffset, buf.byteLength), dec = new TextDecoder();
  let fin = -1;
  for (let i = buf.length - 22; i >= Math.max(0, buf.length - 65557); i--) if (v.getUint32(i, true) === 0x06054b50) { fin = i; break; }
  if (fin < 0) throw new Error('El archivo no es un Excel (.xlsx) válido.');
  const n = v.getUint16(fin + 10, true);
  let p = v.getUint32(fin + 16, true);
  const out = new Map<string, Uint8Array>();
  for (let k = 0; k < n; k++) {
    if (v.getUint32(p, true) !== 0x02014b50) throw new Error('El archivo Excel está dañado.');
    const metodo = v.getUint16(p + 10, true), tam = v.getUint32(p + 20, true), lnom = v.getUint16(p + 28, true), lext = v.getUint16(p + 30, true), lcom = v.getUint16(p + 32, true), local = v.getUint32(p + 42, true);
    const nombre = dec.decode(buf.subarray(p + 46, p + 46 + lnom));
    const ini = local + 30 + v.getUint16(local + 26, true) + v.getUint16(local + 28, true), datos = buf.subarray(ini, ini + tam);
    if (!nombre.endsWith('/')) out.set(nombre, metodo === 0 ? datos : metodo === 8 ? await descomprimir(datos) : new Uint8Array());
    p += 46 + lnom + lext + lcom;
  }
  return out;
}

const entidades = (s: string) => s.replace(/&(#x[0-9a-f]+|#\d+|amp|lt|gt|quot|apos);/gi, (_, e: string) =>
  e[0] === '#' ? String.fromCodePoint(e[1].toLowerCase() === 'x' ? parseInt(e.slice(2), 16) : +e.slice(1)) : ({ amp: '&', lt: '<', gt: '>', quot: '"', apos: "'" } as Record<string, string>)[e.toLowerCase()]);
const textoDe = (xmlSi: string) => entidades([...xmlSi.replace(/<rPh\b[\s\S]*?<\/rPh>/g, '').matchAll(/<t\b[^>]*>([\s\S]*?)<\/t>/g)].map(m => m[1]).join(''));
const attr = (s: string, a: string) => s.match(new RegExp('\\b' + a + '="([^"]*)"'))?.[1];
const colIndice = (ref: string) => { let n = 0; for (const ch of ref.replace(/\d+$/, '')) n = n * 26 + (ch.charCodeAt(0) - 64); return n - 1; };
// Número de serie de Excel (días desde 1899-12-30) a fecha ISO.
export const fechaDeSerie = (n: number) => new Date(Math.round((n - 25569) * 864e5)).toISOString().slice(0, 10);

function estilosFecha(xml: string): boolean[] {
  const propios = new Map([...xml.matchAll(/<numFmt\b[^>]*>/g)].map(m => [+(attr(m[0], 'numFmtId') || 0), entidades(attr(m[0], 'formatCode') || '')]));
  const esFecha = (id: number) => (id >= 14 && id <= 22) || (id >= 45 && id <= 47) || /(^|[^\\"])[dmy]/i.test((propios.get(id) || '').replace(/"[^"]*"|\[[^\]]*\]/g, ''));
  const xfs = xml.match(/<cellXfs\b[\s\S]*?<\/cellXfs>/)?.[0] || '';
  return [...xfs.matchAll(/<xf\b[^>]*>/g)].map(m => esFecha(+(attr(m[0], 'numFmtId') || 0)));
}

export async function leerXlsx(buf: Uint8Array): Promise<HojaLeida[]> {
  const z = await abrirZip(buf), dec = new TextDecoder(), txt = (n: string) => (z.has(n) ? dec.decode(z.get(n)) : '');
  const compartidos = [...txt('xl/sharedStrings.xml').matchAll(/<si\b[^>]*>([\s\S]*?)<\/si>/g)].map(m => textoDe(m[1]));
  const fechas = estilosFecha(txt('xl/styles.xml'));
  const rels = new Map([...txt('xl/_rels/workbook.xml.rels').matchAll(/<Relationship\b[^>]*>/g)].map(m => [attr(m[0], 'Id') || '', attr(m[0], 'Target') || '']));
  const hojas: HojaLeida[] = [];
  for (const m of txt('xl/workbook.xml').matchAll(/<sheet\b[^>]*>/g)) {
    const destino = rels.get(attr(m[0], 'r:id') || '') || '', ruta = destino.startsWith('/') ? destino.slice(1) : 'xl/' + destino.replace(/^\.\//, '');
    const filas: CeldaLeida[][] = [];
    for (const f of txt(ruta).matchAll(/<row\b([^>]*?)(?:\/>|>([\s\S]*?)<\/row>)/g)) {
      const fila: CeldaLeida[] = [], r = +(attr(f[1], 'r') || filas.length + 1) - 1;
      for (const c of (f[2] || '').matchAll(/<c\b([^>]*?)(?:\/>|>([\s\S]*?)<\/c>)/g)) {
        const t = attr(c[1], 't'), ref = attr(c[1], 'r'), s = +(attr(c[1], 's') || 0), cuerpo = c[2] || '';
        const vv = cuerpo.match(/<v>([\s\S]*?)<\/v>/)?.[1];
        let val: CeldaLeida = null;
        if (t === 's') val = vv != null ? compartidos[+vv] ?? '' : null;
        else if (t === 'inlineStr') val = textoDe(cuerpo);
        else if (t === 'str' || t === 'e') val = vv != null ? entidades(vv) : null;
        else if (t === 'b') val = vv === '1' ? 'VERDADERO' : 'FALSO';
        else if (vv != null && vv !== '') { const n = +vv; val = Number.isFinite(n) && fechas[s] ? fechaDeSerie(n) : n; }
        fila[ref ? colIndice(ref) : fila.length] = val;
      }
      filas[r] = Array.from(fila, x => x ?? null);
    }
    hojas.push({ nombre: entidades(attr(m[0], 'name') || 'Hoja'), filas: Array.from(filas, x => x || []) });
  }
  return hojas;
}

// CSV con coma, punto y coma o tabulador (Excel en español guarda con punto y coma).
export function leerCsv(texto: string, nombre = 'CSV'): HojaLeida {
  const t = texto.replace(/^﻿/, ''), primera = t.split(/\r?\n/, 1)[0] || '';
  const sep = [';', ',', '\t'].map(s => [s, primera.split(s).length] as const).sort((a, b) => b[1] - a[1])[0][0];
  const filas: string[][] = [];
  let fila: string[] = [], campo = '', comillas = false;
  for (let i = 0; i < t.length; i++) {
    const ch = t[i];
    if (comillas) { if (ch === '"') { if (t[i + 1] === '"') { campo += '"'; i++; } else comillas = false; } else campo += ch; continue; }
    if (ch === '"') comillas = true;
    else if (ch === sep) { fila.push(campo); campo = ''; }
    else if (ch === '\n' || ch === '\r') { if (ch === '\r' && t[i + 1] === '\n') i++; fila.push(campo); filas.push(fila); fila = []; campo = ''; }
    else campo += ch;
  }
  if (campo || fila.length) { fila.push(campo); filas.push(fila); }
  return { nombre, filas: filas.map(f => f.map(c => (c.trim() === '' ? null : c))) };
}

export async function leerArchivo(nombre: string, datos: Uint8Array): Promise<HojaLeida[]> {
  if (/\.csv$/i.test(nombre)) return [leerCsv(new TextDecoder().decode(datos), nombre.replace(/\.csv$/i, ''))];
  if (/\.vcf$/i.test(nombre)) return [leerVcf(new TextDecoder().decode(datos), nombre.replace(/\.vcf$/i, ''))];
  if (/\.xls$/i.test(nombre) || (datos[0] === 0xd0 && datos[1] === 0xcf)) throw new Error(`"${nombre}" es un Excel antiguo (.xls). Ábrelo en Excel y guárdalo como .xlsx.`);
  return leerXlsx(datos);
}
